import { api, REPORT_RUN_TIMEOUT_MS } from './client'

/**
 * Spec 190 (CC-54303): Exago ("Advanced") reports run inside Report Center through CB's
 * CC-53794 endpoints. cb-api decides which firm sees which report (an allowlist), so
 * for every firm but the allowlisted ones `list()` is an empty array.
 *
 * Spec 209: on a tenant whose site runs CC-54456 (and with cb-api's EXAGO_GENERIC_REPORTS
 * on) the list is the user's own Advanced Reports, `info()` describes one of them, and a
 * run of one that is not the reviewed report answers the GENERIC shape (`kind: 'generic'`,
 * spec 209 §5). Everything else is unchanged.
 */

/**
 * Spec 198: listing starts an Exago session for the user on CB before it reads the folder
 * tree, and cb-api waits up to 45 s for it (LIST_TIMEOUT_MS). The client's 30 s default cut
 * a slow answer off first — and a failed list showed nothing at all.
 */
export const EXAGO_LIST_TIMEOUT_MS = 60_000

/**
 * Spec 209 §5: `/info` reads the user's list (≤ 45 s, as /list does, after a capability
 * probe of up to 5 s) and then RunInfo + DesignInfo (≤ 10 s). The 30 s default would cut a
 * cold answer off.
 */
export const EXAGO_INFO_TIMEOUT_MS = 75_000

// ---- spec 209: shape guards ------------------------------------------------------------
//
// cb-api has already proved the structure of Exago's output (spec 209 §8) before it answers.
// These guards prove only that the answer is the shape the page and the grid builder read,
// and refuse anything else rather than drawing a partial or empty report from it: a money
// cell with printed text and no amount, a row of the wrong width, a total line whose
// figures cannot be shown. A visible refusal beats a plausible wrong number.

const unreadable = (what) => new Error(`${what} came back in a shape we could not read.`)
const INFO_UNREADABLE = 'The report’s description'
const RUN_UNREADABLE = 'The report'

const isObj = (v) => v != null && typeof v === 'object' && !Array.isArray(v)
const isStr = (v) => typeof v === 'string'
const isNonEmptyStr = (v) => isStr(v) && v.trim() !== ''
const isCount = (v) => Number.isInteger(v) && v >= 0
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const isIsoDate = (v) => isStr(v) && ISO_DATE.test(v) && Number.isFinite(Date.parse(`${v}T00:00:00`))

/** Exago's own column kinds (spec 209 §8.6). */
export const EXAGO_COLUMN_KINDS = Object.freeze(['text', 'money', 'number', 'date', 'percent'])
const FIGURE_KINDS = new Set(['money', 'number', 'percent'])
// Only an amount or a count may be re-added (spec 209 §8.8); a percentage never is.
const SUMMABLE_KINDS = new Set(['money', 'number'])
const ALIGNS = new Set(['LEFT', 'RIGHT', 'CENTER'])
const BASES = new Set(['cash', 'accrual', 'unknown'])
const AS_OF_MODES = new Set(['fixed', 'run-date'])
/** A column's printed places: a whole number 0..20, matching cb-api's cap. */
export const MAX_DECIMALS = 20

const readLabelledValue = (p) => isObj(p) && isStr(p.label) && isStr(p.value)

const readDateLabel = (d) => d === null || (isObj(d) && isStr(d.label))

const readNotes = (n) => isObj(n)
  && (n.topN === null || (isObj(n.topN) && isStr(n.topN.action) && Number.isInteger(n.topN.count) && n.topN.count > 0))
  && Array.isArray(n.sorts) && n.sorts.every((s) => isObj(s) && isStr(s.label) && typeof s.ascending === 'boolean')
  && isCount(n.moreSorts)
  && typeof n.usesToday === 'boolean'

/**
 * GET /reports/exago/:id/info → `{ reviewed: true, report }` for the reviewed report (spec
 * 190's page), or the generic description (spec 209 §5). Throws on any other shape.
 */
export function readExagoInfo(body) {
  if (!isObj(body) || typeof body.reviewed !== 'boolean') throw unreadable(INFO_UNREADABLE)
  if (!isObj(body.report) || !isNonEmptyStr(body.report.id) || !isStr(body.report.name)) throw unreadable(INFO_UNREADABLE)
  if (body.reviewed) return { reviewed: true, report: { id: body.report.id, name: body.report.name } }
  const ok = isNonEmptyStr(body.layoutSignature)
    && isObj(body.dates) && readDateLabel(body.dates.asOf) && readDateLabel(body.dates.earliest)
    && Array.isArray(body.fixed) && body.fixed.every(readLabelledValue)
    && Array.isArray(body.cbParameters) && body.cbParameters.every(isStr)
    && readNotes(body.notes)
  if (!ok) throw unreadable(INFO_UNREADABLE)
  return body
}

const readColumns = (cols) => {
  if (!Array.isArray(cols) || cols.length === 0) return false
  const keys = new Set()
  for (const c of cols) {
    if (!isObj(c) || !isNonEmptyStr(c.key) || keys.has(c.key)) return false
    keys.add(c.key)
    if (!isStr(c.header) || !EXAGO_COLUMN_KINDS.includes(c.kind) || !ALIGNS.has(c.align)) return false
    // A whole number of places, 0..20 — cb-api's cap.
    if (!(c.decimals === null || (Number.isInteger(c.decimals) && c.decimals >= 0 && c.decimals <= MAX_DECIMALS))) return false
    if (typeof c.sum !== 'boolean') return false
    // cb-api decides re-adds; one it says yes to on a percent/text/date column is a contract break.
    if (c.sum && !SUMMABLE_KINDS.has(c.kind)) return false
  }
  return true
}

/**
 * One printed row. `figures` (records and footer lines): every money/number/percent cell
 * Exago printed carries its amount, and in a RECORD every printed date its ISO day, because
 * the grid shows amounts from `value` and never Exago's money text (spec 209 §10). A footer's
 * date column may hold a label ("Total") and stays text (§8.6).
 */
const readLine = (line, cols, { record }) => {
  if (!Array.isArray(line) || line.length !== cols.length) return false
  return line.every((cell, i) => {
    if (!isObj(cell) || !isStr(cell.text)) return false
    const kind = cols[i].kind
    const printed = cell.text.trim() !== ''
    if (FIGURE_KINDS.has(kind) && printed && !(typeof cell.value === 'number' && Number.isFinite(cell.value))) return false
    if (kind === 'date' && record && printed && !isIsoDate(cell.iso)) return false
    return true
  })
}

const readFooter = (f, cols) => {
  if (f === null || f === undefined) return true
  if (!isObj(f) || typeof f.hasTotal !== 'boolean' || !Array.isArray(f.lines) || !Array.isArray(f.percentLines)) return false
  if (f.hasTotal && f.lines.length === 0) return false
  // Percent lines are shown as Exago printed them (their own table), so only their width matters.
  return f.lines.every((l) => readLine(l, cols, { record: false }))
    && f.percentLines.every((l) => Array.isArray(l) && l.length === cols.length && l.every((c) => isObj(c) && isStr(c.text)))
}

/**
 * The tree (spec 209 §8): a group is `{ label, children, footer }`, a record `{ cells }`.
 * Siblings are all groups or all records — records sit at the innermost level only (§8.5);
 * a mix would be re-ordered by the grid (leaves before groups), so it is refused.
 */
const readTree = (nodes, cols, tally) => {
  if (!Array.isArray(nodes)) return false
  let groups = 0
  let records = 0
  for (const n of nodes) {
    if (!isObj(n)) return false
    if (Array.isArray(n.cells) && n.children === undefined) {
      if (!readLine(n.cells, cols, { record: true })) return false
      records += 1
    } else if (Array.isArray(n.children) && n.cells === undefined && isStr(n.label)) {
      if (!readFooter(n.footer, cols) || !readTree(n.children, cols, tally)) return false
      groups += 1
    } else {
      return false
    }
  }
  if (groups && records) return false
  tally.records += records
  tally.groups += groups
  return true
}

const readParameters = (p) => isObj(p)
  && isIsoDate(p.asOf) && AS_OF_MODES.has(p.asOfMode)
  && isIsoDate(p.earliest) && typeof p.earliestDefaulted === 'boolean'
  && isObj(p.labels) && (p.labels.asOf === null || isStr(p.labels.asOf)) && (p.labels.earliest === null || isStr(p.labels.earliest))
  && isObj(p.derived)
  && (p.derived.currentStartDate === undefined || isIsoDate(p.derived.currentStartDate))
  && (p.derived.prevEndDate === undefined || isIsoDate(p.derived.prevEndDate))
  && (p.derived.basis === undefined || BASES.has(p.derived.basis))
  && Array.isArray(p.fixed) && p.fixed.every(readLabelledValue)

/**
 * A generic run's answer (spec 209 §5), as `run()` returns it and as a queued run stores it.
 * Throws unless it is exactly the shape the page and `exagoGenericGrid.js` read.
 */
export function readGenericExagoRun(data) {
  if (!isObj(data) || data.kind !== 'generic') throw unreadable(RUN_UNREADABLE)
  const cols = data.columns
  const tally = { records: 0, groups: 0 }
  const ok = isObj(data.report) && isNonEmptyStr(data.report.id) && isStr(data.report.name)
    && readParameters(data.parameters)
    && isNonEmptyStr(data.layoutSignature)
    && readColumns(cols)
    && readTree(data.tree, cols, tally)
    && readFooter(data.reportFooter, cols)
    && Array.isArray(data.headerLines) && data.headerLines.every(isStr)
    && isObj(data.counts) && isCount(data.counts.records) && isCount(data.counts.groups)
    && readNotes(data.notes)
    && typeof data.empty === 'boolean'
  if (!ok) throw unreadable(RUN_UNREADABLE)
  // Cross-checks, so the page's counts, empty state and "grouped" flag can never describe
  // a different tree than the grid draws. `counts.groups` may count top groups or every
  // level, so only its zero-ness is compared.
  if (data.counts.records !== tally.records) throw unreadable(RUN_UNREADABLE)
  if ((data.counts.groups > 0) !== (tally.groups > 0)) throw unreadable(RUN_UNREADABLE)
  if (data.empty !== (tally.records === 0)) throw unreadable(RUN_UNREADABLE)
  return data
}

// ---- spec 209 §10: every /info and /run refusal, as a sentence ---------------------------

/*
 * cb-api answers `{ error, code, ...extra }` (exago/routes.js `refuse`). The page says its
 * OWN sentence per code — the codes are the contract — and adds only the two specifics
 * cb-api states on purpose: CB's reference id (EXAGO_METADATA_FAILED, EXAGO_RUN_FAILED) and
 * the prompt Report Center cannot state (EXAGO_PROMPT_UNSTATED, `prompt`). Every sentence is
 * rendered as React text, never HTML. `d.context` is what was asked: 'info' (the report's
 * description) or 'run'.
 */
const REFUSAL_SENTENCES = {
  // cb-api answers this one code for several different "no"s: the id is not in the user's
  // list, generic reports are off, the site does not support CC-54456 (the probe, or the
  // list's own flag), or the site was downgraded after a metadata 404. The sentence must be
  // true of all of them, so it names both kinds of cause and blames neither.
  EXAGO_REPORT_NOT_AVAILABLE: () => 'This Advanced Report isn’t available to you in Report Center. It may not be shared with you in Exago, or Report Center can’t open Advanced Reports for your firm yet.',
  EXAGO_NOT_SHARED: () => 'This Advanced Report isn’t shared with your account in Exago.',
  EXAGO_FORBIDDEN: () => 'Your Centerbase account can’t open Advanced Reports: your security profile needs Reports → “Can Access Exago Reports”. Ask your Centerbase administrator to turn it on.',
  EXAGO_ACCESS_UNCONFIRMED: () => 'Report Center couldn’t confirm that this report is shared with you in Exago, so it hasn’t opened it.',
  // The capability probe was inconclusive (spec 209 §4): not a "no", so it asks for a retry.
  EXAGO_UNAVAILABLE: () => 'Report Center couldn’t reach Advanced Reports on your Centerbase site just now. Please try again in a minute.',
  // Spec 192 (cb-api tabAuth.js): a saved Advanced Report run read by anyone but its owner.
  EXAGO_OWNER_ONLY: () => 'An Advanced Report run can be opened or changed only by the person it belongs to.',
  EXAGO_METADATA_FAILED: (d) => `Exago couldn’t describe this report${d.reference ? ` (reference ${d.reference})` : ''}.`,
  EXAGO_METADATA_UNREADABLE: () => 'Exago described this report in a way Report Center can’t read safely, so it won’t run it here.',
  EXAGO_PROMPT_UNSTATED: (d) => `This report asks for ${isNonEmptyStr(d.label) ? `“${d.label}”` : 'a value'}, which Report Center can’t show or set yet, so it won’t run the report without saying what it ran with.`,
  EXAGO_LAYOUT_UNSUPPORTED: () => 'Report Center can’t read this report’s printed layout safely, so it won’t show an approximation of it.',
  EXAGO_UNREADABLE: () => 'Exago returned output Report Center couldn’t read, so nothing is shown.',
  EXAGO_RUN_FAILED: (d) => `Exago couldn’t run this report${d.reference ? ` (reference ${d.reference})` : ''}.`,
  EXAGO_TIMEOUT: (d) => (d.context === 'info'
    ? 'Exago took too long to describe this report.'
    : 'Exago took too long to run this report.'),
  EXAGO_BAD_PARAMETERS: () => 'Exago didn’t accept these dates.',
}

/** CB's reference ids are GUIDs; anything else cb-api might carry is not repeated. */
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * `{ code, status, sentence, handOff }` for a failed `/info` or `/run`, or null when the
 * failure is not cb-api's (a network error, a shape guard above): the caller then says its
 * own sentence. `handOff` = a 422, i.e. the report runs in Advanced Reports but Report
 * Center will not draw it — the page offers "Open Advanced Reports".
 *
 * `context` names what was asked — 'info' (the report's description) or 'run' — where a
 * sentence differs by it (EXAGO_TIMEOUT: "describe" vs "run"). Anything else reads as 'run',
 * so a caller that names none keeps the run wording.
 */
export function exagoRefusal(err, context = 'run') {
  const status = err?.response?.status
  const data = isObj(err?.response?.data) ? err.response.data : {}
  const code = isStr(data.code) ? data.code : null
  if (status == null) return null
  // The unstated prompt's label: cb-api sends it as `prompt` (spec 209 §5 names no field;
  // `label` is read too, so a rename on either side still says which prompt).
  const prompt = isStr(data.prompt) ? data.prompt : (isStr(data.label) ? data.label : null)
  const detail = {
    reference: isStr(data.reference) && GUID.test(data.reference) ? data.reference : null,
    label: prompt ? prompt.slice(0, 200) : null,
    context: context === 'info' ? 'info' : 'run',
  }
  const say = code && REFUSAL_SENTENCES[code]
  return {
    code,
    status,
    sentence: say ? say(detail) : 'Report Center couldn’t open this Advanced Report.',
    handOff: status === 422,
  }
}

const exagoReportService = {
  /**
   * GET /reports/exago/list →
   * `{ reports: [{ id, name, summary }], configured, unavailable, blocked, admin }`.
   * `configured` (spec 198): this firm has Advanced Reports in Report Center, so the
   * category shows even when this user has none of them listed.
   * `unavailable` means cb-api could not ask CB this time — not that the firm has none.
   * `blocked` (spec 190 §9) says why an allowlisted firm's user has none:
   * 'exago-permission' (CB refused: Reports → "Can Access Exago Reports" is off for them)
   * or 'not-listed' (Exago does not list the report to them). Null otherwise.
   * `admin` (spec 198): with 'exago-permission', whether CB says this user is a firm
   * administrator (CB refuses administrators too without the permission).
   * `generic` (spec 209): present, and true, only when the list is the user's own Advanced
   * Reports list; each report then also carries `reviewed`, `folder` and `type`, passed
   * through as cb-api sent them.
   */
  async list() {
    const data = await api.get('/reports/exago/list', undefined, { timeout: EXAGO_LIST_TIMEOUT_MS })
    const body = data?.data
    if (!body || !Array.isArray(body.reports)) {
      throw new Error('The advanced reports list came back in a shape we could not read.')
    }
    return {
      reports: body.reports,
      configured: body.configured === true,
      unavailable: body.unavailable === true,
      blocked: typeof body.blocked === 'string' ? body.blocked : null,
      admin: body.admin === true,
      // Only when true, so a list from a worker without spec 209 reads exactly as before.
      ...(body.generic === true ? { generic: true } : {}),
    }
  },

  /**
   * Spec 209: GET /reports/exago/:id/info → `{ reviewed: true, report }` for the reviewed
   * report, else the generic description (dates, read-only prompts, notes). See
   * `readExagoInfo`.
   */
  async info(reportId) {
    const data = await api.get(`/reports/exago/${encodeURIComponent(reportId)}/info`, undefined, { timeout: EXAGO_INFO_TIMEOUT_MS })
    return readExagoInfo(data?.data)
  },

  /**
   * GET /reports/exago/:id/run?asOf=&earliest= → the reconciled report (see cb-api
   * exago/routes.js). Exago takes ~30 s for this report, so this uses the report-run
   * timeout, not the 30 s default that would cut it off mid-run.
   *
   * Spec 209: `{ generic: true }` asks for a generic run and accepts only the generic
   * answer. It always sends the end date; the start date only when the user chose one, so
   * CB applies its own defaults (CB ties the report's current period to the start date,
   * and its 1/1/1900 default sent back would move that period to 1900). The answer states
   * which dates CB used. Without `generic`, spec 190's request and shape, byte for byte.
   */
  async run(reportId, { asOf, earliest } = {}, { generic = false } = {}) {
    if (generic && !asOf) {
      throw new Error('The end date is needed to run this report.')
    }
    const params = { asOf, ...(earliest ? { earliest } : {}) }
    const data = await api.get(`/reports/exago/${encodeURIComponent(reportId)}/run`, params, { timeout: REPORT_RUN_TIMEOUT_MS })
    const report = data?.data
    if (generic) return readGenericExagoRun(report)
    if (!report || !Array.isArray(report.attorneys) || !report.grandTotal) {
      throw new Error('The report came back in a shape we could not read.')
    }
    return report
  },
}

export default exagoReportService
